import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Input,
  Select,
  DatePicker,
  Space,
  Tag,
  Modal,
  Form,
  message,
  Tooltip,
  Badge,
  Row,
  Col,
  Statistic,
  Tabs,
  Image,
  Descriptions,
  Switch,
  Popconfirm
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  ReloadOutlined,
  FilterOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import './EntryExitRecords.css';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;

interface EntryExitRecord {
  _id: string;
  recordId: string;
  
  // 序號：紀錄的流水編號，有助於管理排序
  serialNumber: number;
  
  // 所有車輛：停車場車輛類型說明，如臨停車、月租車等
  vehicleCategory: 'temporary' | 'monthly' | 'vip' | 'resident' | 'visitor' | 'disabled' | 'electric';
  
  // 車牌號碼：車輛的牌照號碼，用於辨識每台車
  plateNumber: string;
  
  // 停車型態：如臨停、月租，區分費率管理
  parkingType: 'temporary' | 'monthly' | 'hourly' | 'daily' | 'free';
  
  // 停車費率：依車輛型態與時段計費
  parkingRate: {
    rateType: 'hourly' | 'daily' | 'monthly' | 'fixed' | 'free';
    amount: number;
    currency: string;
    timeUnit: 'minute' | 'hour' | 'day' | 'month';
  };
  
  // 餘額：月租車的剩餘車資或儲值金額
  balance: {
    currentBalance: number;
    previousBalance: number;
    currency: string;
    lastUpdated: string;
  };
  
  // 發行類型：表示進場時是臨時票或月租卡等
  ticketType: 'temporary_ticket' | 'monthly_card' | 'vip_card' | 'resident_card' | 'visitor_pass' | 'electronic_tag';
  
  // 進場時間：車輛進入停車場的時間
  entryTime: string;
  
  // 進場通道：車輛進場之閘道編號或名稱
  entryGate: {
    gateId: string;
    gateName: string;
    gateType: 'entry' | 'exit' | 'bidirectional';
  };
  
  // 出場時間：車輛出場的時間
  exitTime?: string;
  
  // 出場通道：車輛離場所用的閘道編號或名稱
  exitGate?: {
    gateId: string;
    gateName: string;
    gateType: 'entry' | 'exit' | 'bidirectional';
  };
  
  // 停車時長：計算進出時間，顯示停放總時間
  parkingDuration: {
    totalMinutes: number;
    displayFormat: string;
    calculatedAt: string;
  };
  
  // 停車區域：車輛實際停放的區域
  parkingArea: {
    areaId?: string;
    areaName: string;
    floor?: string;
    zone?: string;
    spotNumber?: string;
    spotType: 'regular' | 'vip' | 'disabled' | 'electric' | 'visitor';
  };
  
  // 車主：車主帳號或登記用戶
  owner: {
    ownerId?: string;
    ownerName: string;
    accountNumber?: string;
    membershipId?: string;
    membershipType: 'regular' | 'vip' | 'monthly' | 'annual' | 'visitor';
  };
  
  // 聯繫電話：車主登記聯繫方式
  contactPhone: string;
  
  // 操作作業：主要紀錄操作人員帳號（如 admin）
  operator: {
    operatorId: string;
    operatorName: string;
    operationType: 'automatic' | 'manual' | 'system' | 'admin';
    operationTime: string;
  };
  
  // 卡片編碼：進出用卡的序號或設備 ID
  cardInfo: {
    cardId?: string;
    cardNumber?: string;
    cardType: 'rfid' | 'magnetic' | 'qr_code' | 'license_plate' | 'mobile_app';
    deviceId?: string;
    isActive: boolean;
  };
  
  // 付款信息
  paymentInfo: {
    totalAmount: number;
    paidAmount: number;
    paymentMethod: 'cash' | 'credit_card' | 'debit_card' | 'mobile_payment' | 'monthly_pass' | 'balance_deduction' | 'free';
    paymentStatus: 'pending' | 'paid' | 'failed' | 'refunded' | 'waived' | 'partial';
    paymentTime?: string;
    transactionId?: string;
    receiptNumber?: string;
  };
  
  // 系統狀態
  status: 'active' | 'completed' | 'cancelled' | 'error' | 'pending';
  
  // 驗證信息
  verification: {
    isVerified: boolean;
    verifiedBy?: string;
    verificationTime?: string;
    verificationMethod: 'automatic' | 'manual' | 'system';
    confidence: number;
  };
  
  // 異常記錄
  anomalies: Array<{
    type: 'overstay' | 'payment_issue' | 'card_error' | 'gate_malfunction' | 'suspicious_activity';
    description: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    isResolved: boolean;
    resolvedBy?: string;
    resolvedTime?: string;
  }>;
  
  // 圖片記錄
  images: Array<{
    imageId?: string;
    imageUrl: string;
    imageType: 'entry_photo' | 'exit_photo' | 'plate_photo' | 'parking_spot_photo';
    timestamp: string;
  }>;
  
  // 時間戳
  createdAt: string;
  updatedAt: string;
}

interface Statistics {
  basic: {
    totalRecords: number;
    verifiedRecords: number;
    successfulRecords: number;
    avgProcessingTime: number;
    avgConfidence: number;
  };
  timeGrouped: Array<{
    _id: string;
    count: number;
    verified: number;
    successful: number;
    avgConfidence: number;
  }>;
  byLane: Array<{
    _id: string;
    laneName: string;
    vehicleType: string;
    direction: string;
    count: number;
    verified: number;
    avgConfidence: number;
  }>;
  byVehicleType: Array<{
    _id: string;
    count: number;
    verified: number;
    avgConfidence: number;
  }>;
}

const EntryExitRecords: React.FC = () => {
  const [records, setRecords] = useState<EntryExitRecord[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<EntryExitRecord | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [verifyModalVisible, setVerifyModalVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    plateNumber: '',
    laneId: '',
    vehicleType: '',
    direction: '',
    status: '',
    isVerified: undefined as boolean | undefined,
    dateRange: null as [dayjs.Dayjs, dayjs.Dayjs] | null
  });

  const [form] = Form.useForm();

  useEffect(() => {
    fetchRecords();
    fetchStatistics();
  }, [pagination.current, pagination.pageSize, filters]);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.current.toString(),
        limit: pagination.pageSize.toString()
      });

      // 添加篩選條件
      if (filters.plateNumber) params.append('plateNumber', filters.plateNumber);
      if (filters.laneId) params.append('laneId', filters.laneId);
      if (filters.vehicleType) params.append('vehicleType', filters.vehicleType);
      if (filters.direction) params.append('direction', filters.direction);
      if (filters.status) params.append('status', filters.status);
      if (filters.isVerified !== undefined) params.append('isVerified', filters.isVerified.toString());
      if (filters.dateRange) {
        params.append('startDate', filters.dateRange[0].toISOString());
        params.append('endDate', filters.dateRange[1].toISOString());
      }

      const response = await fetch(`/api/entry-exit/records?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRecords(data.data.records);
        setPagination(prev => ({
          ...prev,
          total: data.data.pagination.total
        }));
      } else {
        message.error('獲取記錄失敗');
      }
    } catch (error) {
      console.error('獲取記錄錯誤:', error);
      message.error('獲取記錄失敗');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.dateRange) {
        params.append('startDate', filters.dateRange[0].toISOString());
        params.append('endDate', filters.dateRange[1].toISOString());
      }

      const response = await fetch(`/api/entry-exit/statistics?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStatistics(data.data);
      }
    } catch (error) {
      console.error('獲取統計數據錯誤:', error);
    }
  };

  const handleVerifyRecord = async (recordId: string, isVerified: boolean, correctedPlateNumber?: string, notes?: string) => {
    try {
      const response = await fetch(`/api/entry-exit/records/${recordId}/verify`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          isVerified,
          correctedPlateNumber,
          notes
        })
      });

      if (response.ok) {
        message.success('記錄驗證成功');
        fetchRecords();
        setVerifyModalVisible(false);
        form.resetFields();
      } else {
        message.error('驗證失敗');
      }
    } catch (error) {
      console.error('驗證錯誤:', error);
      message.error('驗證失敗');
    }
  };

  const handleBatchVerify = async (isVerified: boolean) => {
    if (selectedRowKeys.length === 0) {
      message.warning('請選擇要驗證的記錄');
      return;
    }

    try {
      const response = await fetch('/api/entry-exit/records/batch-verify', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          recordIds: selectedRowKeys,
          isVerified,
          notes: `批量${isVerified ? '通過' : '拒絕'}`
        })
      });

      if (response.ok) {
        const data = await response.json();
        message.success(`成功驗證 ${data.data.modifiedCount} 條記錄`);
        fetchRecords();
        setSelectedRowKeys([]);
      } else {
        message.error('批量驗證失敗');
      }
    } catch (error) {
      console.error('批量驗證錯誤:', error);
      message.error('批量驗證失敗');
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.dateRange) {
        params.append('startDate', filters.dateRange[0].toISOString());
        params.append('endDate', filters.dateRange[1].toISOString());
      }
      if (filters.laneId) params.append('laneId', filters.laneId);
      if (filters.vehicleType) params.append('vehicleType', filters.vehicleType);
      params.append('format', 'csv');

      const response = await fetch(`/api/entry-exit/export?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `entry-exit-records-${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        message.success('導出成功');
      } else {
        message.error('導出失敗');
      }
    } catch (error) {
      console.error('導出錯誤:', error);
      message.error('導出失敗');
    }
  };

  const getStatusTag = (status: string) => {
    const statusConfig = {
      pending: { color: 'orange', text: '待處理' },
      verified: { color: 'green', text: '已驗證' },
      rejected: { color: 'red', text: '已拒絕' },
      processing: { color: 'blue', text: '處理中' }
    };
    const config = statusConfig[status as keyof typeof statusConfig] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getVehicleTypeTag = (type: string) => {
    return type === 'motorcycle' ? 
      <Tag color="blue">機車</Tag> : 
      <Tag color="green">汽車</Tag>;
  };

  const getDirectionTag = (direction: string) => {
    return direction === 'entry' ? 
      <Tag color="cyan">進入</Tag> : 
      <Tag color="purple">離開</Tag>;
  };

  const getConfidenceTag = (confidence: number) => {
    let color = 'red';
    if (confidence >= 0.8) color = 'green';
    else if (confidence >= 0.6) color = 'orange';
    
    return <Tag color={color}>{(confidence * 100).toFixed(1)}%</Tag>;
  };

  const columns = [
    {
      title: '序號',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      width: 80,
      render: (text: number, record: EntryExitRecord, index: number) => (
        <span style={{ fontWeight: 'bold' }}>
          {text || (pagination.current - 1) * pagination.pageSize + index + 1}
        </span>
      )
    },
    {
      title: '所有車輛',
      dataIndex: 'vehicleCategory',
      key: 'vehicleCategory',
      width: 100,
      render: (category: string) => {
        const categoryMap = {
          'temporary': { text: '臨停車', color: 'blue' },
          'monthly': { text: '月租車', color: 'green' },
          'vip': { text: 'VIP車', color: 'gold' },
          'resident': { text: '住戶車', color: 'purple' },
          'visitor': { text: '訪客車', color: 'orange' },
          'disabled': { text: '身障車', color: 'red' },
          'electric': { text: '電動車', color: 'cyan' }
        };
        const config = categoryMap[category as keyof typeof categoryMap] || { text: category, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '車牌號碼',
      dataIndex: 'plateNumber',
      key: 'plateNumber',
      width: 120,
      render: (text: string) => (
        <span style={{ fontWeight: 'bold', fontSize: '14px' }}>{text}</span>
      )
    },
    {
      title: '停車型態',
      dataIndex: 'parkingType',
      key: 'parkingType',
      width: 100,
      render: (type: string) => {
        const typeMap = {
          'temporary': { text: '臨停', color: 'blue' },
          'monthly': { text: '月租', color: 'green' },
          'hourly': { text: '時租', color: 'orange' },
          'daily': { text: '日租', color: 'purple' },
          'free': { text: '免費', color: 'cyan' }
        };
        const config = typeMap[type as keyof typeof typeMap] || { text: type, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '停車費率',
      dataIndex: 'parkingRate',
      key: 'parkingRate',
      width: 120,
      render: (rate: any) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            NT$ {rate?.amount || 0}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {rate?.rateType || '-'} / {rate?.timeUnit || '-'}
          </div>
        </div>
      )
    },
    {
      title: '餘額',
      dataIndex: 'balance',
      key: 'balance',
      width: 100,
      render: (balance: any) => (
        <div>
          <div style={{ fontWeight: 'bold', color: balance?.currentBalance > 0 ? '#52c41a' : '#ff4d4f' }}>
            NT$ {balance?.currentBalance || 0}
          </div>
          <div style={{ fontSize: '11px', color: '#999' }}>
            {balance?.lastUpdated ? dayjs(balance.lastUpdated).format('MM-DD') : '-'}
          </div>
        </div>
      )
    },
    {
      title: '發行類型',
      dataIndex: 'ticketType',
      key: 'ticketType',
      width: 120,
      render: (type: string) => {
        const ticketMap = {
          'temporary_ticket': { text: '臨時票', color: 'blue' },
          'monthly_card': { text: '月租卡', color: 'green' },
          'vip_card': { text: 'VIP卡', color: 'gold' },
          'resident_card': { text: '住戶卡', color: 'purple' },
          'visitor_pass': { text: '訪客證', color: 'orange' },
          'electronic_tag': { text: '電子標籤', color: 'cyan' }
        };
        const config = ticketMap[type as keyof typeof ticketMap] || { text: type, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '進場時間',
      dataIndex: 'entryTime',
      key: 'entryTime',
      width: 140,
      render: (time: string) => (
        <span>{time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-'}</span>
      )
    },
    {
      title: '進場通道',
      dataIndex: 'entryGate',
      key: 'entryGate',
      width: 120,
      render: (gate: any) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{gate?.gateName || '-'}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            ID: {gate?.gateId || '-'}
          </div>
        </div>
      )
    },
    {
      title: '出場時間',
      dataIndex: 'exitTime',
      key: 'exitTime',
      width: 140,
      render: (time: string) => (
        <span>{time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-'}</span>
      )
    },
    {
      title: '出場通道',
      dataIndex: 'exitGate',
      key: 'exitGate',
      width: 120,
      render: (gate: any) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{gate?.gateName || '-'}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            ID: {gate?.gateId || '-'}
          </div>
        </div>
      )
    },
    {
      title: '停車時長',
      dataIndex: 'parkingDuration',
      key: 'parkingDuration',
      width: 120,
      render: (duration: any) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            {duration?.displayFormat || '-'}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {duration?.totalMinutes ? `${duration.totalMinutes}分鐘` : '-'}
          </div>
        </div>
      )
    },
    {
      title: '停車區域',
      dataIndex: 'parkingArea',
      key: 'parkingArea',
      width: 140,
      render: (area: any) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{area?.areaName || '-'}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {area?.floor && `${area.floor}樓`}
            {area?.zone && ` ${area.zone}區`}
            {area?.spotNumber && ` ${area.spotNumber}號`}
          </div>
        </div>
      )
    },
    {
      title: '車主',
      dataIndex: 'owner',
      key: 'owner',
      width: 120,
      render: (owner: any) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{owner?.ownerName || '-'}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {owner?.membershipType && (
              <Tag color="blue">{owner.membershipType}</Tag>
            )}
          </div>
        </div>
      )
    },
    {
      title: '聯繫電話',
      dataIndex: 'contactPhone',
      key: 'contactPhone',
      width: 120,
      render: (phone: string) => (
        <span>{phone || '-'}</span>
      )
    },
    {
      title: '操作作業',
      dataIndex: 'operator',
      key: 'operator',
      width: 140,
      render: (operator: any) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{operator?.operatorName || 'system'}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            <Tag color={operator?.operationType === 'manual' ? 'orange' : 'green'}>
              {operator?.operationType || 'automatic'}
            </Tag>
          </div>
        </div>
      )
    },
    {
      title: '卡片編碼',
      dataIndex: 'cardInfo',
      key: 'cardInfo',
      width: 140,
      render: (card: any) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{card?.cardNumber || '-'}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            <Tag 
              color={card?.isActive ? 'green' : 'red'}
            >
              {card?.cardType || '-'}
            </Tag>
          </div>
        </div>
      )
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      render: (record: EntryExitRecord) => (
        <Space size="small">
          <Tooltip title="查看詳情">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedRecord(record);
                setDetailModalVisible(true);
              }}
            />
          </Tooltip>
          {!record.verification.isVerified && (
            <>
              <Tooltip title="驗證通過">
                <Button
                  type="text"
                  icon={<CheckOutlined />}
                  style={{ color: '#52c41a' }}
                  onClick={() => handleVerifyRecord(record._id, true)}
                />
              </Tooltip>
              <Tooltip title="拒絕">
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  style={{ color: '#ff4d4f' }}
                  onClick={() => handleVerifyRecord(record._id, false)}
                />
              </Tooltip>
            </>
          )}
        </Space>
      )
    }
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => {
      setSelectedRowKeys(keys as string[]);
    },
    getCheckboxProps: (record: EntryExitRecord) => ({
      disabled: record.verification.isVerified
    })
  };

  return (
    <div className="entry-exit-records">
      <Card title="進出入記錄管理" className="main-card">
        <Tabs defaultActiveKey="records">
          <TabPane tab="記錄列表" key="records">
            {/* 統計卡片 */}
            {statistics && (
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title="總記錄數"
                      value={statistics.basic.totalRecords}
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title="已驗證記錄"
                      value={statistics.basic.verifiedRecords}
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title="平均信心度"
                      value={statistics.basic.avgConfidence}
                      precision={2}
                      suffix="%"
                      valueStyle={{ color: '#722ed1' }}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title="平均處理時間"
                      value={statistics.basic.avgProcessingTime}
                      precision={0}
                      suffix="ms"
                      valueStyle={{ color: '#fa8c16' }}
                    />
                  </Card>
                </Col>
              </Row>
            )}

            {/* 篩選器 */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={4}>
                  <Input
                    placeholder="車牌號碼"
                    value={filters.plateNumber}
                    onChange={(e) => setFilters(prev => ({ ...prev, plateNumber: e.target.value }))}
                    prefix={<SearchOutlined />}
                  />
                </Col>
                <Col span={3}>
                  <Select
                    placeholder="車輛類型"
                    value={filters.vehicleType}
                    onChange={(value) => setFilters(prev => ({ ...prev, vehicleType: value }))}
                    allowClear
                  >
                    <Option value="motorcycle">機車</Option>
                    <Option value="car">汽車</Option>
                  </Select>
                </Col>
                <Col span={3}>
                  <Select
                    placeholder="方向"
                    value={filters.direction}
                    onChange={(value) => setFilters(prev => ({ ...prev, direction: value }))}
                    allowClear
                  >
                    <Option value="entry">進入</Option>
                    <Option value="exit">離開</Option>
                  </Select>
                </Col>
                <Col span={3}>
                  <Select
                    placeholder="狀態"
                    value={filters.status}
                    onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                    allowClear
                  >
                    <Option value="pending">待處理</Option>
                    <Option value="verified">已驗證</Option>
                    <Option value="rejected">已拒絕</Option>
                  </Select>
                </Col>
                <Col span={6}>
                  <RangePicker
                    value={filters.dateRange}
                    onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates as [dayjs.Dayjs, dayjs.Dayjs] | null }))}
                    showTime
                    format="YYYY-MM-DD HH:mm"
                  />
                </Col>
                <Col span={5}>
                  <Space>
                    <Button
                      type="primary"
                      icon={<SearchOutlined />}
                      onClick={() => {
                        setPagination(prev => ({ ...prev, current: 1 }));
                        fetchRecords();
                      }}
                    >
                      搜尋
                    </Button>
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={() => {
                        setFilters({
                          plateNumber: '',
                          laneId: '',
                          vehicleType: '',
                          direction: '',
                          status: '',
                          isVerified: undefined,
                          dateRange: null
                        });
                        setPagination(prev => ({ ...prev, current: 1 }));
                      }}
                    >
                      重置
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Card>

            {/* 批量操作 */}
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Popconfirm
                  title="確定要批量通過選中的記錄嗎？"
                  onConfirm={() => handleBatchVerify(true)}
                  disabled={selectedRowKeys.length === 0}
                >
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    disabled={selectedRowKeys.length === 0}
                  >
                    批量通過 ({selectedRowKeys.length})
                  </Button>
                </Popconfirm>
                <Popconfirm
                  title="確定要批量拒絕選中的記錄嗎？"
                  onConfirm={() => handleBatchVerify(false)}
                  disabled={selectedRowKeys.length === 0}
                >
                  <Button
                    danger
                    icon={<CloseOutlined />}
                    disabled={selectedRowKeys.length === 0}
                  >
                    批量拒絕 ({selectedRowKeys.length})
                  </Button>
                </Popconfirm>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleExport}
                >
                  導出記錄
                </Button>
              </Space>
            </div>

            {/* 記錄表格 */}
            <Table
              columns={columns}
              dataSource={records}
              rowKey="recordId"
              loading={loading}
              rowSelection={rowSelection}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `第 ${range[0]}-${range[1]} 條，共 ${total} 條`,
                onChange: (page, pageSize) => {
                  setPagination(prev => ({
                    ...prev,
                    current: page,
                    pageSize: pageSize || 20
                  }));
                }
              }}
              scroll={{ x: 2200 }}
            />
          </TabPane>

          <TabPane tab="統計分析" key="statistics">
            {statistics && (
              <div>
                <Row gutter={16} style={{ marginBottom: 24 }}>
                  <Col span={12}>
                    <Card title="按車道統計">
                      <Table
                        dataSource={statistics.byLane}
                        pagination={false}
                        size="small"
                        columns={[
                          { title: '車道', dataIndex: 'laneName', key: 'laneName' },
                          { title: '類型', dataIndex: 'vehicleType', key: 'vehicleType', render: (type) => getVehicleTypeTag(type) },
                          { title: '記錄數', dataIndex: 'count', key: 'count' },
                          { title: '已驗證', dataIndex: 'verified', key: 'verified' },
                          { title: '平均信心度', dataIndex: 'avgConfidence', key: 'avgConfidence', render: (val) => `${(val * 100).toFixed(1)}%` }
                        ]}
                      />
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card title="按車輛類型統計">
                      <Table
                        dataSource={statistics.byVehicleType}
                        pagination={false}
                        size="small"
                        columns={[
                          { title: '車輛類型', dataIndex: '_id', key: 'type', render: (type) => getVehicleTypeTag(type) },
                          { title: '記錄數', dataIndex: 'count', key: 'count' },
                          { title: '已驗證', dataIndex: 'verified', key: 'verified' },
                          { title: '平均信心度', dataIndex: 'avgConfidence', key: 'avgConfidence', render: (val) => `${(val * 100).toFixed(1)}%` }
                        ]}
                      />
                    </Card>
                  </Col>
                </Row>
              </div>
            )}
          </TabPane>
        </Tabs>
      </Card>

      {/* 記錄詳情模態框 */}
      <Modal
        title="記錄詳情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedRecord && (
          <div>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="序號">{selectedRecord.serialNumber}</Descriptions.Item>
              <Descriptions.Item label="記錄ID">{selectedRecord.recordId}</Descriptions.Item>
              
              <Descriptions.Item label="車輛類型">
                {(() => {
                  const categoryMap = {
                    'temporary': '臨停車',
                    'monthly': '月租車',
                    'vip': 'VIP車',
                    'resident': '住戶車',
                    'visitor': '訪客車',
                    'disabled': '身障車',
                    'electric': '電動車'
                  };
                  return categoryMap[selectedRecord.vehicleCategory as keyof typeof categoryMap] || selectedRecord.vehicleCategory;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="車牌號碼">{selectedRecord.plateNumber}</Descriptions.Item>
              
              <Descriptions.Item label="停車型態">
                {(() => {
                  const typeMap = {
                    'temporary': '臨停',
                    'monthly': '月租',
                    'hourly': '時租',
                    'daily': '日租',
                    'free': '免費'
                  };
                  return typeMap[selectedRecord.parkingType as keyof typeof typeMap] || selectedRecord.parkingType;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="停車費率">
                NT$ {selectedRecord.parkingRate?.amount || 0} / {selectedRecord.parkingRate?.timeUnit || '-'}
              </Descriptions.Item>
              
              <Descriptions.Item label="餘額">
                <span style={{ color: selectedRecord.balance?.currentBalance > 0 ? '#52c41a' : '#ff4d4f' }}>
                  NT$ {selectedRecord.balance?.currentBalance || 0}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="發行類型">
                {(() => {
                  const ticketMap = {
                    'temporary_ticket': '臨時票',
                    'monthly_card': '月租卡',
                    'vip_card': 'VIP卡',
                    'resident_card': '住戶卡',
                    'visitor_pass': '訪客證',
                    'electronic_tag': '電子標籤'
                  };
                  return ticketMap[selectedRecord.ticketType as keyof typeof ticketMap] || selectedRecord.ticketType;
                })()}
              </Descriptions.Item>
              
              <Descriptions.Item label="進場時間">
                {selectedRecord.entryTime ? dayjs(selectedRecord.entryTime).format('YYYY-MM-DD HH:mm:ss') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="進場通道">
                {selectedRecord.entryGate?.gateName || '-'} (ID: {selectedRecord.entryGate?.gateId || '-'})
              </Descriptions.Item>
              
              <Descriptions.Item label="出場時間">
                {selectedRecord.exitTime ? dayjs(selectedRecord.exitTime).format('YYYY-MM-DD HH:mm:ss') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="出場通道">
                {selectedRecord.exitGate?.gateName || '-'} (ID: {selectedRecord.exitGate?.gateId || '-'})
              </Descriptions.Item>
              
              <Descriptions.Item label="停車時長">
                {selectedRecord.parkingDuration?.displayFormat || '-'}
                {selectedRecord.parkingDuration?.totalMinutes && (
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    ({selectedRecord.parkingDuration.totalMinutes}分鐘)
                  </div>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="停車區域">
                {selectedRecord.parkingArea?.areaName || '-'}
                {(selectedRecord.parkingArea?.floor || selectedRecord.parkingArea?.zone || selectedRecord.parkingArea?.spotNumber) && (
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {selectedRecord.parkingArea.floor && `${selectedRecord.parkingArea.floor}樓`}
                    {selectedRecord.parkingArea.zone && ` ${selectedRecord.parkingArea.zone}區`}
                    {selectedRecord.parkingArea.spotNumber && ` ${selectedRecord.parkingArea.spotNumber}號`}
                  </div>
                )}
              </Descriptions.Item>
              
              <Descriptions.Item label="車主">
                {selectedRecord.owner?.ownerName || '-'}
                {selectedRecord.owner?.membershipType && (
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    會員類型: {selectedRecord.owner.membershipType}
                  </div>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="聯繫電話">{selectedRecord.contactPhone || '-'}</Descriptions.Item>
              
              <Descriptions.Item label="操作作業">
                {selectedRecord.operator?.operatorName || 'system'}
                <div style={{ fontSize: '12px', color: '#666' }}>
                  類型: {selectedRecord.operator?.operationType || 'automatic'}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="卡片編碼">
                {selectedRecord.cardInfo?.cardNumber || '-'}
                {selectedRecord.cardInfo?.cardType && (
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    類型: {selectedRecord.cardInfo.cardType}
                    <span style={{ color: selectedRecord.cardInfo.isActive ? '#52c41a' : '#ff4d4f' }}>
                      ({selectedRecord.cardInfo.isActive ? '啟用' : '停用'})
                    </span>
                  </div>
                )}
              </Descriptions.Item>
              
              {/* 付款信息 */}
              <Descriptions.Item label="付款金額">
                NT$ {selectedRecord.paymentInfo?.totalAmount || 0}
                {selectedRecord.paymentInfo?.paidAmount !== selectedRecord.paymentInfo?.totalAmount && (
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    已付: NT$ {selectedRecord.paymentInfo?.paidAmount || 0}
                  </div>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="付款狀態">
                <Tag color={
                  selectedRecord.paymentInfo?.paymentStatus === 'paid' ? 'green' :
                  selectedRecord.paymentInfo?.paymentStatus === 'pending' ? 'orange' :
                  selectedRecord.paymentInfo?.paymentStatus === 'failed' ? 'red' :
                  selectedRecord.paymentInfo?.paymentStatus === 'refunded' ? 'blue' : 'default'
                }>
                  {selectedRecord.paymentInfo?.paymentStatus === 'paid' ? '已付款' :
                   selectedRecord.paymentInfo?.paymentStatus === 'pending' ? '待付款' :
                   selectedRecord.paymentInfo?.paymentStatus === 'failed' ? '付款失敗' :
                   selectedRecord.paymentInfo?.paymentStatus === 'refunded' ? '已退款' :
                   selectedRecord.paymentInfo?.paymentStatus === 'waived' ? '免費' :
                   selectedRecord.paymentInfo?.paymentStatus === 'partial' ? '部分付款' : '未知'}
                </Tag>
              </Descriptions.Item>
              
              <Descriptions.Item label="狀態">
                <Tag color={
                  selectedRecord.status === 'completed' ? 'green' :
                  selectedRecord.status === 'active' ? 'blue' :
                  selectedRecord.status === 'pending' ? 'orange' :
                  selectedRecord.status === 'error' ? 'red' : 'default'
                }>
                  {selectedRecord.status === 'completed' ? '已完成' :
                   selectedRecord.status === 'active' ? '進行中' :
                   selectedRecord.status === 'pending' ? '待處理' :
                   selectedRecord.status === 'error' ? '錯誤' :
                   selectedRecord.status === 'cancelled' ? '已取消' : selectedRecord.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="驗證狀態">
                {selectedRecord.verification?.isVerified ? (
                  <div>
                    <Tag color="green">已驗證</Tag>
                    {selectedRecord.verification.verificationTime && (
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        驗證時間: {dayjs(selectedRecord.verification.verificationTime).format('YYYY-MM-DD HH:mm:ss')}
                      </div>
                    )}
                    {selectedRecord.verification.verifiedBy && (
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        驗證人: {selectedRecord.verification.verifiedBy}
                      </div>
                    )}
                  </div>
                ) : (
                  <Tag color="orange">待驗證</Tag>
                )}
              </Descriptions.Item>
            </Descriptions>

            {selectedRecord.images && selectedRecord.images.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <h4>相關圖片</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                  {selectedRecord.images.map((image, index) => {
                    const getImageTypeText = (type: string) => {
                      const typeMap = {
                        'entry_photo': '進場拍照',
                        'exit_photo': '出場拍照',
                        'plate_photo': '車牌拍照',
                        'parking_spot_photo': '停車位拍照'
                      };
                      return typeMap[type as keyof typeof typeMap] || type;
                    };

                    return (
                      <div key={index} style={{ textAlign: 'center' }}>
                        <div style={{ marginBottom: 8 }}>
                          <Tag color="blue">{getImageTypeText(image.imageType)}</Tag>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {dayjs(image.timestamp).format('YYYY-MM-DD HH:mm:ss')}
                          </div>
                        </div>
                        <Image
                          width={200}
                          src={image.imageUrl}
                          style={{ border: '1px solid #d9d9d9', borderRadius: '6px' }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {selectedRecord.anomalies && selectedRecord.anomalies.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <h4>異常信息</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {selectedRecord.anomalies.map((anomaly, index) => {
                    const getSeverityColor = (severity: string) => {
                      const colorMap = {
                        'low': 'blue',
                        'medium': 'orange',
                        'high': 'red',
                        'critical': 'purple'
                      };
                      return colorMap[severity as keyof typeof colorMap] || 'default';
                    };

                    const getTypeText = (type: string) => {
                      const typeMap = {
                        'overstay': '超時停車',
                        'payment_issue': '付款問題',
                        'card_error': '卡片錯誤',
                        'gate_malfunction': '閘門故障',
                        'suspicious_activity': '可疑活動'
                      };
                      return typeMap[type as keyof typeof typeMap] || type;
                    };

                    return (
                      <div key={index} style={{ 
                        padding: '8px 12px', 
                        border: '1px solid #d9d9d9', 
                        borderRadius: '6px',
                        backgroundColor: anomaly.isResolved ? '#f6ffed' : '#fff2f0'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <Tag color={getSeverityColor(anomaly.severity)}>
                              {getTypeText(anomaly.type)}
                            </Tag>
                            <Tag color={anomaly.isResolved ? 'green' : 'red'}>
                              {anomaly.isResolved ? '已解決' : '待處理'}
                            </Tag>
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            嚴重程度: {anomaly.severity}
                          </div>
                        </div>
                        <div style={{ marginTop: '4px', fontSize: '14px' }}>
                          {anomaly.description}
                        </div>
                        {anomaly.isResolved && anomaly.resolvedBy && (
                          <div style={{ marginTop: '4px', fontSize: '12px', color: '#52c41a' }}>
                            解決人: {anomaly.resolvedBy}
                            {anomaly.resolvedTime && ` (${dayjs(anomaly.resolvedTime).format('YYYY-MM-DD HH:mm:ss')})`}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 驗證模態框 */}
      <Modal
        title="驗證記錄"
        open={verifyModalVisible}
        onCancel={() => {
          setVerifyModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            if (selectedRecord) {
              handleVerifyRecord(
                selectedRecord.recordId,
                values.isVerified,
                values.correctedPlateNumber,
                values.notes
              );
            }
          }}
        >
          <Form.Item
            name="isVerified"
            label="驗證結果"
            rules={[{ required: true, message: '請選擇驗證結果' }]}
          >
            <Select placeholder="請選擇驗證結果">
              <Option value={true}>通過</Option>
              <Option value={false}>拒絕</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="correctedPlateNumber"
            label="修正車牌號碼"
            help="如果辨識錯誤，請輸入正確的車牌號碼"
          >
            <Input placeholder="請輸入正確的車牌號碼" />
          </Form.Item>

          <Form.Item
            name="notes"
            label="備註"
          >
            <Input.TextArea rows={3} placeholder="請輸入驗證備註" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                確認驗證
              </Button>
              <Button onClick={() => {
                setVerifyModalVisible(false);
                form.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EntryExitRecords;